import { Alert, Button, FieldBlock, Input, LoadingState, Select, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api.ts'
import { abrirNoNavegadorPadrao } from '../../lib/link-externo.ts'
import './orbind-ai-config.css'

/**
 * Console > Orbind AI (SPEC-20261104-2229): chave da API da Anthropic e modelo
 * do Orbind AI, guardados no banco (a chave cifrada). Salvar uma chave já a
 * testa na Anthropic; a tela só mostra os 4 últimos caracteres.
 */

type Situacao = Awaited<ReturnType<Awaited<ReturnType<typeof api.console['orbind-ai']['$get']>>['json']>>
type Modelo = Situacao['modelo']

const CHAVE_DA_CONSULTA = ['console', 'orbind-ai'] as const

const NOME_DO_MODELO: Record<string, string> = {
  'claude-opus-5-5': 'Claude Opus 5.5 — o mais capaz (recomendado)',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5 — equilíbrio entre custo e qualidade',
  'claude-haiku-5-5': 'Claude Haiku 5.5 — o mais rápido e barato',
}

const erroDe = (erro: unknown) => (erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.')
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

export function OrbindAiConfigSection() {
  const toast = useToast()
  const qc = useQueryClient()
  const [chave, setChave] = useState('')

  const q = useQuery({
    queryKey: CHAVE_DA_CONSULTA,
    queryFn: async () => (await api.console['orbind-ai'].$get()).json(),
  })
  const s = q.data

  const aplicar = (dados: Situacao) => {
    qc.setQueryData(CHAVE_DA_CONSULTA, dados)
    // a tela do Orbind AI deixa de dizer "não configurado"
    void qc.invalidateQueries({ queryKey: ['ai-assistant'] })
  }
  const avisarTeste = (dados: Situacao) => {
    if (dados.erroDoTeste) toast.push({ tone: 'danger', message: dados.erroDoTeste })
    else if (dados.testadaEm) toast.push({ tone: 'success', message: 'Chave conferida com a Anthropic. O Orbind AI já pode conversar.' })
  }

  const salvar = useMutation({
    mutationFn: async (corpo: { chave?: string; modelo?: Modelo }) => (await api.console['orbind-ai'].$put({ json: corpo })).json(),
    onSuccess: (dados, corpo) => {
      aplicar(dados)
      if (corpo.chave) setChave('')
      avisarTeste(dados)
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const testar = useMutation({
    mutationFn: async () => (await api.console['orbind-ai'].testar.$post()).json(),
    onSuccess: (dados) => {
      aplicar(dados)
      avisarTeste(dados)
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const remover = useMutation({
    mutationFn: async () => (await api.console['orbind-ai'].chave.$delete()).json(),
    onSuccess: (dados) => {
      aplicar(dados)
      toast.push({ tone: 'success', message: 'Chave removida do Console.' })
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Orbind AI</h1>
        <p className="adm-head__desc">
          A chave da API da Anthropic que o Orbind AI usa para conversar, e o modelo. A chave fica guardada cifrada no banco
          e vale para a instalação inteira; quem pode usar o Orbind AI continua sendo definido em cada workspace, na tela
          Conectores de IA.
        </p>
      </div>
    </div>
  )

  if (q.isPending) return <LoadingState variant="detail" label="Carregando a configuração do Orbind AI" />
  if (q.isError || !s) {
    return (
      <>
        {cabecalho}
        <Alert
          tone="danger"
          action={
            <Button size="sm" icon="fas fa-redo" onClick={() => void q.refetch()}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar a configuração do Orbind AI.
        </Alert>
      </>
    )
  }

  const chaveDigitada = chave.trim()
  const ocupado = salvar.isPending || testar.isPending || remover.isPending

  return (
    <div className="oai">
      {cabecalho}

      <section className="oai-cartao" aria-labelledby="oai-situacao">
        <h2 id="oai-situacao" className="oai-cartao__titulo">
          Situação
        </h2>
        {s.origem === null ? (
          <Alert tone="warning">
            <strong>Sem chave.</strong> O Orbind AI aparece para as pessoas como "ainda não configurado" até uma chave ser
            salva aqui.
          </Alert>
        ) : s.erroDoTeste ? (
          <Alert tone="danger">
            <strong>A chave não funcionou no último teste.</strong> {s.erroDoTeste}
          </Alert>
        ) : s.testadaEm ? (
          <Alert tone="success">
            <strong>Funcionando.</strong> Chave conferida com a Anthropic em {dataHora(s.testadaEm)}.
          </Alert>
        ) : (
          <Alert tone="info">
            <strong>Chave ainda não testada.</strong> Clique em "Testar chave" para conferir com a Anthropic.
          </Alert>
        )}
        <dl className="oai-dados">
          <div>
            <dt>Chave em uso</dt>
            <dd>
              {s.origem === 'console' ? (
                <>
                  <i className="fas fa-lock" aria-hidden /> Salva no Console, terminando em <code>…{s.finalDaChave}</code>
                </>
              ) : s.origem === 'ambiente' ? (
                <>
                  <i className="fas fa-server" aria-hidden /> Variável ANTHROPIC_API_KEY do servidor
                </>
              ) : (
                'Nenhuma'
              )}
            </dd>
          </div>
          {s.atualizadaEm ? (
            <div>
              <dt>Última alteração</dt>
              <dd>{dataHora(s.atualizadaEm)}</dd>
            </div>
          ) : null}
        </dl>
        {s.origem !== null ? (
          <div className="oai-acoes">
            <Button size="sm" icon="fas fa-vial" loading={testar.isPending} disabled={ocupado} onClick={() => testar.mutate()}>
              Testar chave
            </Button>
            {s.origem === 'console' ? (
              <Button
                size="sm"
                variant="ghost"
                icon="far fa-trash-alt"
                loading={remover.isPending}
                disabled={ocupado}
                onClick={() => remover.mutate()}
              >
                {s.temVariavelDoServidor ? 'Remover e usar a do servidor' : 'Remover chave'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="oai-cartao" aria-labelledby="oai-chave">
        <h2 id="oai-chave" className="oai-cartao__titulo">
          {s.origem === 'console' ? 'Trocar a chave' : 'Cadastrar a chave'}
        </h2>
        <ol className="oai-passos">
          <li>
            Entre no console da Anthropic com a conta da empresa e abra <strong>API Keys</strong>.{' '}
            <button type="button" className="oai-link" onClick={() => void abrirNoNavegadorPadrao('https://console.anthropic.com/settings/keys')}>
              Abrir o console da Anthropic <i className="fas fa-external-link-alt" aria-hidden />
            </button>
          </li>
          <li>
            Clique em <strong>Create Key</strong>, dê um nome (por exemplo, "Orbind AI") e copie a chave — ela começa com{' '}
            <code>sk-ant-</code> e só aparece uma vez.
          </li>
          <li>Cole abaixo e clique em "Salvar e testar".</li>
        </ol>
        <form
          className="oai-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (chaveDigitada) salvar.mutate({ chave: chaveDigitada })
          }}
        >
          <FieldBlock label="Chave da API" hint="Guardada cifrada. Depois de salva, só os 4 últimos caracteres aparecem aqui.">
            {({ id, invalid }) => (
              <Input
                id={id}
                invalid={invalid}
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="sk-ant-…"
                value={chave}
                onChange={(e) => setChave(e.target.value)}
              />
            )}
          </FieldBlock>
          <div className="oai-acoes">
            <Button type="submit" variant="primary" icon="fas fa-save" loading={salvar.isPending && chaveDigitada !== ''} disabled={!chaveDigitada || ocupado}>
              Salvar e testar
            </Button>
          </div>
        </form>
      </section>

      <section className="oai-cartao" aria-labelledby="oai-modelo">
        <h2 id="oai-modelo" className="oai-cartao__titulo">
          Modelo
        </h2>
        <FieldBlock label="Modelo do Orbind AI" hint="Vale para a próxima mensagem de todo mundo.">
          {({ id }) => (
            <Select<Modelo>
              id={id}
              value={s.modelo}
              disabled={ocupado}
              options={s.modelos.map((m) => ({ value: m, label: NOME_DO_MODELO[m] ?? m }))}
              onChange={(modelo) => {
                if (modelo !== s.modelo) salvar.mutate({ modelo })
              }}
            />
          )}
        </FieldBlock>
      </section>
    </div>
  )
}
